<script lang="ts">
import {defineComponent, PropType} from 'vue'
import Select, {SelectFilterEvent} from 'primevue/select'
import {fetchWrapper} from "@/helpers/fetchWrapper"
import backendRoutes from "@/backendRoutes"
import {ApiPlatformParser} from "@/helpers/apiPlatformParser"
import {Student} from "@/interfaces/student"
import debounce from "debounce"
import isNull from "@/helpers/isNull"
import MultiSelect from "primevue/multiselect"

export default defineComponent({
  name: "StudentSelect",
  components: {
    Select,
    MultiSelect
  },
  props: {
    invalid: {
      type: Boolean,
      required: false,
      default: false
    },
    modelValue: {
      type: [Object, Array] as PropType<Student | Student[] | null>,
      required: false,
      default: null
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    listRoute: {
      type: String,
      required: false,
      default: backendRoutes.student_list
    },
    requestParams: {
      type: Object,
      required: false,
      default: undefined
    }
  },
  data() {
    return {
      students: [] as Student[],
      search: '' as string,
      debouncedFetchStudentListApi: debounce(() => {
        this.fetchStudentList(true)
      }, 300),
      isLoading: true as boolean,
      isLoadingMore: false as boolean,
      studentSelectedByUrl: this.$route.query.studentSelect ? JSON.parse(String(this.$route.query.studentSelect)) : null as Student | null,
      page: 1 as number,
      itemsPerPage: 50 as number,
      totalItems: 0 as number
    }
  },
  mounted() {
    if(this.studentSelectedByUrl) {
      this.$emit('update:modelValue', this.studentSelectedByUrl)
    }
    this.fetchStudentList(true)
  },
  watch: {
    modelValue: {
      deep: true,
      handler() {
        this.ensureSelectedValuesInOptions()
      }
    }
  },
  computed: {
    virtualScrollerOptions(): Record<string, any> {
      return {
        lazy: true,
        onLazyLoad: this.onLazyLoad,
        itemSize: 38,
        showLoader: true,
        loading: this.isLoadingMore,
      }
    }
  },
  methods: {
    isNull,
    decorateStudentForSearch(student: Student | null): Student | null {
      if (!student) {
        return student
      }
      const cleanup = (value?: string | null) => (value ?? '').trim()
      const removeSpaces = (value: string) => value.replace(/\s+/g, '')

      const lastname = cleanup(student.name)
      const firstname = cleanup(student.firstname)
      const extendedStudent = {...student} as Student & Record<string, string>
      extendedStudent.fullName = `${lastname} ${firstname}`.trim()
      extendedStudent.reverseFullName = `${firstname} ${lastname}`.trim()
      extendedStudent.fullNameNoSpace = `${removeSpaces(lastname)}${removeSpaces(firstname)}`
      extendedStudent.reverseFullNameNoSpace = `${removeSpaces(firstname)}${removeSpaces(lastname)}`
      return extendedStudent
    },
    decorateStudentsList(students: Student[]): Student[] {
      return students.map((student: Student) => this.decorateStudentForSearch(student) as Student)
    },
    async fetchStudentList(reset = false) {
      if (reset) {
        this.isLoading = true
        this.page = 1
        this.totalItems = 0
        this.students = []
      } else {
        if (this.isLoading || this.isLoadingMore) return
        if (this.students.length >= this.totalItems && this.totalItems !== 0) return
        this.isLoadingMore = true
      }
      const response = await fetchWrapper.get(this.listRoute, undefined,
          {
            search: this.search,
            page: this.page,
            itemsPerPage: this.itemsPerPage,
            ...this.requestParams
          })
      const apiPlatformParser = new ApiPlatformParser(response, ApiPlatformParser.LIST_TYPE)
      const newStudents = this.decorateStudentsList(apiPlatformParser.getData())
      this.totalItems = apiPlatformParser.getTotalItemCount()
      if (reset) {
        this.students = newStudents
      } else {
        this.students = [...this.students, ...newStudents]
      }
      this.page += 1
      this.isLoading = false
      this.isLoadingMore = false
      this.ensureSelectedValuesInOptions()
    },
    searchTipping(event: SelectFilterEvent) {
      this.search = event.value
      this.debouncedFetchStudentListApi()
    },
    ensureSelectedValuesInOptions() {
      if(this.multiple) {
        if(Array.isArray(this.modelValue) && this.modelValue.length > 0) {
          const missing = this.modelValue.filter((student: Student) => student && !this.students.find((s: Student) => s.id === student.id))
          if(missing.length > 0) {
            this.students = [...this.students, ...this.decorateStudentsList(missing)]
          }
        }
      } else if(this.modelValue && !Array.isArray(this.modelValue)) {
        const selectedStudent = this.decorateStudentForSearch(this.modelValue as Student) as Student
        if(selectedStudent.id !== undefined) {
          const exists = this.students.find((student: Student) => student.id === selectedStudent.id)
          if(!exists) {
            this.students = [...this.students, selectedStudent]
          }
        } else {
          this.students = [...this.students, selectedStudent]
        }
      }
    },
    emitValue(value: Student | Student[]) {
      this.$emit('update:modelValue', value)
    },
    onLazyLoad(event: { first: number, last: number }) {
      if (event.first === 0) return
      if (this.isLoading || this.isLoadingMore) return
      if (this.students.length >= this.totalItems && this.totalItems !== 0) return
      if (event.last < this.students.length - 5) return
      this.fetchStudentList(false)
    }
  },
  emits: ['update:modelValue'],
})
</script>

<template>
  <Select v-if="!multiple" @filter="searchTipping" :modelValue="modelValue" filter :filterFields="['firstname','name','fullName','reverseFullName','fullNameNoSpace','reverseFullNameNoSpace','id']"
          :invalid="invalid" :options="students" @update:modelValue="emitValue" :showClear="!isNull(modelValue)"
          :virtualScrollerOptions="virtualScrollerOptions">
    <template #value="slotProps">
      <div v-if="slotProps.value && slotProps.value.firstname">{{ slotProps.value.name }} {{ slotProps.value.firstname }}</div>
    </template>
    <template #option="slotProps">
      <div>{{ slotProps.option.name }} {{ slotProps.option.firstname }}</div>
    </template>
    <template #emptyfilter>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.student_select.empty") }}</p>
      </div>
    </template>
    <template #empty>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.student_select.empty") }}</p>
      </div>
    </template>
  </Select>
  <MultiSelect v-else @filter="searchTipping" :modelValue="modelValue" filter :filterFields="['firstname','name','fullName','reverseFullName','fullNameNoSpace','reverseFullNameNoSpace','id']"
               :invalid="invalid" :options="students" @update:modelValue="emitValue" :dataKey="'id'"
               :virtualScrollerOptions="virtualScrollerOptions">
    <template #value="slotProps">
      <div v-if="slotProps.value && slotProps.value.length === 1">
        {{ slotProps.value[0].name }} {{ slotProps.value[0].firstname }}
      </div>
      <div v-else-if="slotProps.value && slotProps.value.length > 1">
        {{ slotProps.value.length }} {{ $t('admin_components.student_select.student_selected') }}
      </div>
    </template>
    <template #option="slotProps">
      <div>{{ slotProps.option.name }} {{ slotProps.option.firstname }}</div>
    </template>
    <template #emptyfilter>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.student_select.empty") }}</p>
      </div>
    </template>
    <template #empty>
      <div v-if="isLoading" style="text-align: center">
        <i class="pi pi-spin pi-spinner loader" ></i>
      </div>
      <div v-else>
        <p>{{ $t("admin_components.student_select.empty") }}</p>
      </div>
    </template>
  </MultiSelect>
</template>
